import type { ElementType, MouseEvent, ReactNode } from 'react';
import { cn } from '@helpers/cn';
import { ConditionalWrapper, Tooltip } from '@eye-able/ui';

type SidebarNavButtonProps = {
  component?: ElementType;
  level?: number;
  selected?: boolean;
  disabled?: boolean;
  className?: string;
  children?: ReactNode;
  onClick?: (event: MouseEvent<HTMLElement>) => void;
  tooltip?: string;
  [key: string]: unknown;
};

function getPaddingLeft(level: number) {
  const safeLevel = Number.isFinite(level) ? Math.max(1, level) : 1;
  return safeLevel === 1 ? 48 : safeLevel * 40;
}

function SidebarNavButton({
  component: Component = 'button',
  level = 1,
  selected = false,
  disabled = false,
  className = '',
  onClick,
  tooltip,
  ...restProps
}: SidebarNavButtonProps) {
  const isButton = Component === 'button';

  const handleClick = (event: MouseEvent<HTMLElement>) => {
    if (disabled) {
      event.preventDefault();
      event.stopPropagation();
      return;
    }

    onClick?.(event);
  };

  return (
    <ConditionalWrapper
      condition={!!tooltip}
      wrapper={(children) => (
        <Tooltip content={tooltip} side='right' sideOffset={12}>
          {children}
        </Tooltip>
      )}
    >
      <Component
        {...restProps}
        type={isButton ? ((restProps.type as string) ?? 'button') : undefined}
        disabled={isButton ? disabled : undefined}
        aria-disabled={!isButton && disabled ? true : undefined}
        onClick={handleClick}
        style={{ paddingLeft: getPaddingLeft(level) }}
        className={cn(
          'eye:flex eye:w-full eye:min-h-[48px] eye:cursor-pointer eye:py-12 eye:pr-12',
          'eye:hover:bg-grey-10 eye:items-center eye:text-14 eye:font-medium eye:focus-visible:outline-black eye:focus-visible:-outline-offset-1',
          selected && 'eye:bg-yellow-100 eye:hover:bg-yellow-100',
          disabled && 'eye:cursor-not-allowed eye:opacity-50',
          className,
        )}
      />
    </ConditionalWrapper>
  );
}

export default SidebarNavButton;
